import {
    IConnectionService,
    ILoginManager,
    IPreviousAccount,
    LOGIN_METHODS_ALL,
    LOGIN_METHODS_AUTOMATIC,
    LOGIN_METHODS_INTERACTIVE,
    LoginMethods,
    SubscribeState,
    getLoginManager,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { upsert } from "~/utils";
import { makeAccount } from "./account";
import { makeApprove } from "./approve";
import { makeBudget } from "./budget";
import { makeClient } from "./client";
import { makeClientStorage } from "./client-storage";
import { makeCompany } from "./company";
import { makeCost } from "./cost";
import { makeCurrency } from "./currency";
import { onConnection } from "./debug";
import { makeDiscovery } from "./discovery";
import { makeEmployee } from "./employee";
import { makeExpense } from "./expense";
import { makeFeeCategory } from "./fee-category";
import { makeFeeCode } from "./fee-code";
import { makeGroup } from "./group";
import { LoginParams } from "./interfaces";
import { makeMe } from "./me";
import { makeOtherCode } from "./other-code";
import { makeProInvoiceText } from "./pro-invoice-text";
import { makeProject } from "./project";
import { makePurchase } from "./purchase";
import { makePurchaseCode } from "./purchase-code";
import { makeRelation } from "./relation";
import { makeService } from "./service";
import { makeSettings } from "./settings";
import { makeStatus } from "./status";
import { makeSupplier } from "./supplier";
import { makeTask } from "./task";
import { makeTime } from "./time";
import { makeTodo } from "./todo";
import { clearObjectUrls, isSameDomain } from "./utils";
import { makeVatClass } from "./vat-class";

const ALL_SCOPES_READONLY = [
    "pro.time.read",
    "pro.time.insert",
    "pro.time.update",
    "pro.time.delete",
    "pro.projects.read",
    "pro.projects.insert",
    "pro.projects.update",
    "pro.projects.delete",
    "pro.clients.read",
    "pro.groups.read",
    "pro.budgets.read",
    "pro.budgets.insert",
    "pro.budgets.update",
    "pro.budgets.delete",
    "pro.employees.read",
    "pro.fee_codes.read",
    "pro.purchase_codes.read",
    "pro.other_codes.read",
    "pro.fee_categories.read",
    "pro.employees.insert",
    "pro.employees.update",
] as const;
const INSTALLATION_PLACEHOLDER = "_";

void ALL_SCOPES_READONLY;

let loginManagerPromise: Promise<ILoginManager>;
let loginAbortController: AbortController | null = null;

const getUseRedirect = (continuePath?: string) => {
    if (!continuePath) {
        return "dashboard";
    }

    const parts = continuePath.split("/").filter(Boolean);

    if (parts[0] === "me") {
        const finalParts = parts.slice(3);

        return finalParts.length ? finalParts.join("/") : "dashboard";
    }

    return "dashboard";
};

const makeApiSliceParams = (
    connection: ReadonlySignal<IConnectionService | null>,
    company: ReadonlySignal<string>,
) => ({
    company: {
        get value() {
            return company.peek();
        },
    },
    connection: {
        get value() {
            const con = connection.peek();

            if (!con) {
                throw new Error("Connection is not set");
            }

            return con;
        },
    },
});

const makeApi = () => {
    const connecting = signal(true);
    const waiting = signal(false);
    const waitingAccount = signal("");
    const connection = signal<IConnectionService | null>(null);
    const installation = signal("");
    const companyId = signal("");
    const accounts = signal<IPreviousAccount[]>([]);
    const accountsLoaded = signal(false);
    const isLoB = computed(() => connection.value?.user.lob ?? false);
    const sliceParams = makeApiSliceParams(connection, companyId);
    const project = makeProject(sliceParams);
    const budget = makeBudget(sliceParams);
    const task = makeTask(sliceParams);
    const employee = makeEmployee(sliceParams);
    const feeCode = makeFeeCode(sliceParams);
    const feeCategory = makeFeeCategory(sliceParams);
    const purchaseCode = makePurchaseCode(sliceParams);
    const otherCode = makeOtherCode(sliceParams);
    const time = makeTime(sliceParams);
    const status = makeStatus(sliceParams);
    const purchase = makePurchase(sliceParams);
    const client = makeClient(sliceParams);
    const service = makeService(sliceParams);
    const group = makeGroup(sliceParams);
    const discovery = makeDiscovery(sliceParams);
    const settings = makeSettings(sliceParams);
    const clientStorage = makeClientStorage(sliceParams);
    const expense = makeExpense(sliceParams);
    const currency = makeCurrency(sliceParams);
    const account = makeAccount(sliceParams);
    const approve = makeApprove(sliceParams);
    const supplier = makeSupplier(sliceParams);
    const vatClass = makeVatClass(sliceParams);
    const company = makeCompany(sliceParams);
    const cost = makeCost(sliceParams);
    const todo = makeTodo(sliceParams);
    const relation = makeRelation(sliceParams);
    const proInvoiceText = makeProInvoiceText(sliceParams);
    const me = makeMe(sliceParams);

    const onConnected = () => {
        service.listen();
        onConnection(connection.peek());
    };

    const login = async ({
        installation: installationCode,
        continuePath,
    }: LoginParams) => {
        if (loginAbortController) {
            loginAbortController.abort();
        }

        waiting.value = true;
        loginAbortController = new AbortController();

        try {
            const lm = await loginManagerPromise;
            const con = await lm.attemptLogin(
                {
                    methods: LOGIN_METHODS_INTERACTIVE,
                    redirectPath: getUseRedirect(continuePath),
                    abortSignal: loginAbortController.signal,
                },
                installationCode,
            );

            if (con) {
                connection.value = con;
                installation.value = con.user.domain;
                companyId.value = con.user.company_id;
                onConnected();
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.name === "AbortError") {
                return;
            }

            console.error("[api] login error", error);
        }

        waiting.value = false;
        loginAbortController = null;
    };

    const loginAs = async (
        account: IPreviousAccount,
        continuePath?: string | undefined,
        methods?: LoginMethods | undefined,
    ) => {
        if (loginAbortController) {
            loginAbortController.abort();
        }

        waiting.value = true;
        waitingAccount.value = account.sub;
        loginAbortController = new AbortController();

        try {
            const con = await account.login({
                methods: methods ?? LOGIN_METHODS_ALL,
                redirectPath: getUseRedirect(continuePath),
                abortSignal: loginAbortController.signal,
            });

            if (con) {
                connection.value = con;
                installation.value = con.user.domain;
                companyId.value = con.user.company_id;
                onConnected();
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.name === "AbortError") {
                return;
            }

            console.error("[api] loginAs error", error);
        }

        waiting.value = false;
        waitingAccount.value = "";
        loginAbortController = null;
    };

    const connectAny = async (installationCode?: string) => {
        if (loginAbortController) {
            loginAbortController.abort();
        }

        loginAbortController = new AbortController();

        const lm = await loginManagerPromise;
        const con = await lm.attemptLogin(
            {
                methods: LOGIN_METHODS_AUTOMATIC,
                abortSignal: loginAbortController.signal,
            },
            installationCode,
        );

        if (con) {
            connection.value = con;
            installation.value = con.user.domain;
            companyId.value = con.user.company_id;
            onConnected();
        }
    };

    const connect = async (installationCode?: string) => {
        if (!accountsLoaded.peek()) {
            await new Promise<void>((resolve) => {
                const unsubscribe = accountsLoaded.subscribe((loaded) => {
                    if (loaded) {
                        resolve();
                        unsubscribe();
                    }
                });
            });
        }

        try {
            const now = Date.now();
            const sorted = accounts
                .peek()
                .toSorted((a, b) => b.lastLogin - a.lastLogin);
            let account = sorted.find(
                (account) =>
                    (installationCode
                        ? isSameDomain(account.domain, installationCode)
                        : true) && account.refreshExpires > now - 3000,
            );

            if (
                !account &&
                installationCode === INSTALLATION_PLACEHOLDER &&
                sorted.length &&
                Math.abs(now - sorted[0].lastLogin) <= 12_000
            ) {
                account = sorted[0];
            }

            if (account) {
                await loginAs(account, undefined, LOGIN_METHODS_AUTOMATIC);
            } else if (installationCode !== INSTALLATION_PLACEHOLDER) {
                await connectAny(installationCode);
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.name === "AbortError") {
                return;
            }

            console.error("[api] connect error", error);
        }

        connecting.value = false;
        loginAbortController = null;
    };

    const cleanup = () => {
        clearObjectUrls();
    };

    const logout = async () => {
        cleanup();
        connecting.value = false;
        connection.value = null;
    };

    const listenAccounts = async () => {
        if (accountsLoaded.peek()) {
            return;
        }

        try {
            const lm = await loginManagerPromise;
            const list = lm.listAccounts();

            for await (const event of list) {
                if (event.kind === SubscribeState.Loaded) {
                    accountsLoaded.value = true;
                } else if (
                    event.kind === SubscribeState.Add ||
                    event.kind === SubscribeState.Update
                ) {
                    accounts.value = upsert(
                        accounts.peek().slice(),
                        event.values,
                        "sub",
                    );
                } else if (event.kind === SubscribeState.Remove) {
                    accounts.value = accounts
                        .peek()
                        .filter(
                            (account) =>
                                !event.values.find(
                                    (v) => v.sub === account.sub,
                                ),
                        );
                }
            }
        } catch (error: unknown) {
            console.error("[api] listAccounts error", error);
            return [];
        }
    };

    loginManagerPromise = getLoginManager();
    listenAccounts();

    return {
        isLoB,
        installation,
        companyId,
        connection,
        connecting,
        waiting,
        waitingAccount,
        accounts,
        accountsLoaded,
        project,
        budget,
        task,
        employee,
        feeCode,
        feeCategory,
        purchaseCode,
        otherCode,
        time,
        status,
        purchase,
        client,
        service,
        group,
        discovery,
        expense,
        currency,
        settings,
        account,
        approve,
        supplier,
        vatClass,
        company,
        cost,
        me,
        todo,
        relation,
        proInvoiceText,
        clientStorage,
        connect,
        login,
        loginAs,
        logout,
    };
};

export default makeApi;
